import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { ADMIN_ROLES } from "@/lib/roles";

export default function AdminLogin() {
  const { login, user, authReady } = useApp();
  const nav = useNavigate();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => { if (authReady && user && ADMIN_ROLES.includes(user.role)) nav("/admin/dashboard"); }, [authReady, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(loginId, password);
      if (!ADMIN_ROLES.includes(u.role)) { setErr("This account does not have admin access."); return; }
      nav("/admin/dashboard");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-forest flex items-center justify-center px-4 leaf-pattern">
      <div className="w-full max-w-md bg-cream rounded-[28px] soft-shadow-lg p-8">
        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_URL} alt="LIVENEAT logo" className="h-44 w-auto object-contain mb-3" />
          <h1 className="font-heading text-3xl text-forest">Admin Login</h1>
          <p className="text-sm text-brown/70">LIVENEAT Organic Management</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-sm">Username</Label><Input type="text" value={loginId} onChange={(e) => setLoginId(e.target.value)} required placeholder="Username" className="rounded-xl bg-white" data-testid="admin-login-email" /></div>
          <div>
            <Label className="text-sm">Password</Label>
            <div className="relative">
              <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl bg-white pr-11" data-testid="admin-login-password" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-brown/60 hover:text-forest transition-colors" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="admin-login-password-toggle">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {err && <p className="text-sm text-terracotta" data-testid="admin-login-error">{err}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="admin-login-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}</Button>
        </form>
      </div>
    </div>
  );
}
